import * as React from 'react';
import { SxProps, Theme, styled } from '@mui/material/styles';
import { sportsbookConfig } from './configs/entryManager';
import { downloadCsvFile, getFilename } from '../common/helpers/FilenameHelper';

// components
import Slide from '@mui/material/Slide';
import { Alert, Box, Button, Dialog, DialogContent, FormControl, FormControlLabel, FormLabel, IconButton, Link, Radio, RadioGroup, Stack, Tooltip, Typography } from '@mui/material';
import ContestUpload from './ContestsUpload';
import ConfirmModal from './ConfirmModal';
import ToggleSwitch from '../common/ToggleSwitch';

// types
import { TransitionProps } from '@mui/material/transitions';
import { LineupApiResponse } from './queries/queryTypes';
import { IRootState } from '../../store/reducer';
import { Lineup, LineupRubric } from '../LineupBuilder/store/types';

// icons
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DownloadDoneOutlined from '@mui/icons-material/DownloadDoneOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SvgIcon from '@material-ui/core/SvgIcon/SvgIcon';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DoneIcon from '@mui/icons-material/Done';
import CircularProgress from '@mui/material/CircularProgress';

// hooks
import { useSelector } from 'react-redux';
import { useLocalStorage } from 'usehooks-ts';
import { getExportableLineupsString } from '../utils/exportableLineup';
import { useUserToken } from '../utils/authentication';
import { useGetCSVTemplate, useGetContestSummary } from './queries/entryManagerQueries';
import { useAddLineupsToContest, useFileUpload, useRemoveLineupsFromSlate } from './mutations/entryManagerMutations';

interface EntryContestRowProps {
    buttonSx?: SxProps<Theme>,
    contestName: React.ReactNode,
    contestFee?: number,
    disabled?: boolean,
    endIcon?: React.ReactNode,
    onClick?: () => void,
    onSuccess?: boolean,
    secondaryText?: React.ReactNode,
}

const EntryContestRow = (props: EntryContestRowProps) => {
    const {
        contestName,
        contestFee,
        buttonSx,
        disabled,
        endIcon = false,
        onClick,
        onSuccess,
        secondaryText,
    } = props;
    const defaultButtonSx = {
        borderRadius: '0px 10px 10px 0px',
        boxSizing: 'border-box',
        height: '100%',
        padding: '12px',
        '&.Mui-disabled': {
            opacity: '.3',
            background: '#252525 !important',
            color: "#fff"
        },
    };

    return (
        <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{
                borderRadius: endIcon ? '0px 10px 10px 0px' : '0px',
                backgroundColor: '#101010'
            }}
            color='#FFF'
            height='48px'
            boxSizing='border-box'
        >
            <Stack
                direction='row'
                justifyContent={'space-between'}
                width='100%'
                p='12px 15px'
                height='48px'
                boxSizing='border-box'
                fontFamily='Inter'
                alignItems='center'
                sx={{
                    backgroundColor: '#1C1C1C'
                }}
            >
                <Stack direction='row' alignItems='center' gap='15px' minWidth='0'>
                    <span style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: '650px' }} >{contestName}</span> {contestFee && <span style={{ flexShrink: 0, width: '60px', fontSize: '14px', opacity: '.7' }}>${contestFee?.toFixed(2)}/entry</span>}
                </Stack>
                {
                    secondaryText && <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{secondaryText}</span>
                }
            </Stack>
            {
                onSuccess && (
                    <Box sx={{ background: '#0A0A0A', borderRadius: '0px 10px 10px 0px', height: '100%' }}>
                        <Box sx={{ background: 'rgba(0, 114, 59, 0.30)', ...defaultButtonSx }}>
                            <DoneIcon />
                        </Box>
                    </Box>
                )
            }
            {
                endIcon && (
                    <Tooltip title={disabled ? 'To add lineups to contests, please favorite at least 1 lineup' : ''} placement='left'>
                        <span>
                            <IconButton
                                onClick={onClick}
                                disabled={disabled}
                                color='inherit'
                                sx={[defaultButtonSx, ...(Array.isArray(buttonSx) ? buttonSx : [buttonSx])]} // You cannot spread `sx` directly because `SxProps` (typeof sx) can be an array
                            >
                                {endIcon}
                            </IconButton>
                        </span>
                    </Tooltip>
                )
            }
        </Stack>
    )
}
const DialogHeader = styled('div')(({ theme }) => ({
    color: '#FFFFFF',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 15px'
}));
const DialogActions = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 15px',
    gap: '15px'
}));

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="left" ref={ref} {...props} />;
});
interface LocalFavorites {
    saved: LineupApiResponse[],
    removed: LineupApiResponse[]
}
type SortOption = 'contestFee' | 'entries';

interface EntryManagerProps {
    buttonLabel?: string;
    buttonVariant?: 'text' | 'outlined' | 'contained';
    showLineupCount?: boolean;
    lineups?: Lineup[];
    lineupRubric?: LineupRubric[];
    showIcon?: boolean;
    bgColor?: string;
    localStorageKey?: string;
}

export default function EntryManager(props: EntryManagerProps) {
    const buttonLabel = props.buttonLabel || 'Entry Manager'
    const buttonVariant = props.buttonVariant || 'contained'
    const showLineupCount = props.showLineupCount || false;
    const lineupBuilderLineups = props.lineups || [];
    const userToken = useUserToken();
    const { selectedSlate } = useSelector((state: IRootState) => state.common);
    const sport = selectedSlate?.sport;
    const slateId = selectedSlate?.slateId;
    const site = selectedSlate?.site;
    const localStorageKey = props.localStorageKey || `precontest-lineups-${slateId}`;
    const [open, setOpen] = React.useState(false);
    const [enableDownloadQuery, setEnableDownloadQuery] = React.useState(false);
    const [sortBy, setSortBy] = React.useState<SortOption>('contestFee');
    const [enableDuplicateLineups, setEnableDuplicateLineups] = React.useState(() => {
        const savedValue = sessionStorage.getItem('entryManagerEnableDuplicateLineups');
        return savedValue ? JSON.parse(savedValue) : true;
    });

    const [savedLineups, setSavedLineups] = useLocalStorage<LineupApiResponse[]>(
        localStorageKey,
        []
    );
    const [localSavedLineups, setLocalSavedLineups] = React.useState<LocalFavorites>({ saved: [], removed: [] }); // copy of savedLineups variable because setSavedLineups was rerending from another reference
    const exportableLineups = getExportableLineupsString(localSavedLineups?.saved);

    const sportsbook = sportsbookConfig[site];

    // react-query data
    const { data: contestsData, refetch: refetchContestSummary } = useGetContestSummary(slateId, sport, !open, sortBy)
    const {
        mutate: addCSVFile,
        isSuccess: isFileUploadSuccess,
        isError: isFileUploadError,
        isLoading: isUploading,
        error: fileError,
        reset: resetFile,
    } = useFileUpload();

    const {
        mutate: addLineupsToContest,
        isLoading: isLoadingAdd,
        isSuccess: isAddLineupsSuccess,
    } = useAddLineupsToContest();
    const {
        mutate: removeLineups,
        isLoading: isLoadingRemove,
        isSuccess: isRemoveLineupsSuccess,
    } = useRemoveLineupsFromSlate();
    const {
        data: csvTemplate,
        refetch: refetchCSV
    } = useGetCSVTemplate(sport, slateId, enableDownloadQuery)

    const contestIds = contestsData?.map(contest => contest?.contestId);
    const allContests = contestsData?.reduce((acc, curr) => {
        acc.savedEntries += curr?.savedEntries;
        acc.totalEntries += curr?.totalEntries;
        return acc;
    }, {
        savedEntries: 0,
        totalEntries: 0,
    });

    const hasContests = contestsData?.length > 0;


    const handleToggleSwitch = () => {
        setEnableDuplicateLineups(prev => !prev)
    }

    const handleFileUpload = async (params) => {
        if (params) {
            resetFile(); // Reset mutation state before new upload
            addCSVFile(params, {
                onSuccess: () => {
                    // Manually refetch contest summary after successful upload
                    refetchContestSummary();
                    refetchCSV();
                }
            });
        }
    };

    const handleAddLineupsToContest = async (params) => {
        if (params) {
            addLineupsToContest(params, {
                onSuccess: (data, variables: any) => {
                    const addedLineups = variables?.contests?.[0]?.lineups || [];
                    if (!enableDuplicateLineups) {
                        // lineups that were added and moved from local saved lineups to local removed lineups
                        const updatedRemovedLineups = localSavedLineups?.saved?.filter(lineup => addedLineups?.includes(lineup?.exportableLineup));
                        // remaining lineups after moved to local removed lineups
                        const updatedSavedLineups = localSavedLineups?.saved?.filter(lineup => !addedLineups?.includes(lineup?.exportableLineup));
                        setLocalSavedLineups(prev => ({
                            saved: updatedSavedLineups, removed: [...prev.removed, ...updatedRemovedLineups]
                        }));
                    }
                    // Manually refetch data after adding lineups
                    refetchContestSummary();
                    refetchCSV();
                }
            });
        }
    };

    const handleRemoveLineupsFromContest = async (contestId) => {
        const params = {
            Token: userToken,
            Sport: sport,
            ContestIds: [contestId],
        };
        if (contestId) {
            removeLineups(params, {
                onSuccess: (data) => {
                    const removedLineups = data?.[0]?.lineups;
                    // remaining removed lineups after moved to local saved lineups
                    const updatedRemovedLineups = localSavedLineups?.removed?.filter(lineup => !removedLineups?.includes(lineup?.exportableLineup));
                    // lineups that were removed and added back to local saved lineups
                    const updatedSavedLineups = localSavedLineups?.removed?.filter(lineup => removedLineups?.includes(lineup?.exportableLineup));
                    setLocalSavedLineups(prev => ({
                        saved: [...prev.saved, ...updatedSavedLineups], removed: updatedRemovedLineups
                    }));
                    // Manually refetch data after removing lineups
                    refetchContestSummary();
                    refetchCSV();
                }
            });
        }
    };

    const handleRemoveAllLineups = () => {
        const params = {
            Token: userToken,
            Sport: sport,
            ContestIds: contestIds
        }
        removeLineups(params, {
            onSuccess() {
                setLocalSavedLineups(prev => ({ ...prev, saved: savedLineups }));
                // Manually refetch data after removing all lineups
                refetchContestSummary();
                refetchCSV();
            },
        });
    }

    const downloadCSV = () => {
        if (csvTemplate) {
            const filename = getFilename(selectedSlate, `_Entry_Manager_Lineups`);
            downloadCsvFile(csvTemplate, filename);
        }
    }

    const handleClickOpen = () => {
        setEnableDownloadQuery(true);
        setOpen(true);
    };

    const handleClose = () => {
        setEnableDownloadQuery(false);
        setOpen(false);
        resetFile();
        setSavedLineups(localSavedLineups?.saved)
        setLocalSavedLineups(prev => ({ ...prev, removed: [] }))
    };

    // Handle sort by radio button change
    const handleSortChange = (event) => {
        setSortBy(event.target.value);
    };

    // ui 
    const NoteStackedIconOutline = () => (
        <SvgIcon>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="note_stack_FILL0_wght200_GRAD0_opsz24 1 1">
                    <path id="Vector" d="M6.66677 16.154V8.0081C6.66677 7.63524 6.79871 7.31848 7.0626 7.05779C7.32649 6.79711 7.64486 6.66678 8.01773 6.66678H16.154C16.5268 6.66678 16.8444 6.79791 17.1067 7.06019C17.369 7.32248 17.5001 7.64006 17.5001 8.01292V13.8143L13.8142 17.5001H8.01291C7.64005 17.5001 7.32248 17.369 7.06019 17.1067C6.7979 16.8444 6.66677 16.5268 6.66677 16.154ZM2.52094 5.4969C2.44828 5.12404 2.51985 4.7883 2.73566 4.48969C2.95148 4.19108 3.24582 4.00544 3.61869 3.93279L11.6347 2.52094C12.0076 2.44829 12.3433 2.51986 12.6419 2.73567C12.9406 2.95149 13.1262 3.24583 13.1988 3.61869L13.3751 4.67959H12.5322L12.3542 3.686C12.3329 3.56848 12.2688 3.47499 12.1619 3.40556C12.0551 3.33612 11.9376 3.31208 11.8094 3.33344L3.76612 4.76134C3.61655 4.78269 3.50437 4.85214 3.42958 4.96965C3.3548 5.08718 3.32809 5.22073 3.34945 5.37029L4.67958 12.8831V14.4969C4.48941 14.4039 4.32782 14.272 4.1948 14.1011C4.06179 13.9302 3.97445 13.7341 3.93279 13.5129L2.52094 5.4969ZM7.5001 8.01292V16.154C7.5001 16.3035 7.54818 16.4264 7.64433 16.5226C7.74048 16.6187 7.86334 16.6668 8.01291 16.6668H13.3334V14.0065C13.3334 13.8132 13.3972 13.6526 13.5249 13.5249C13.6526 13.3973 13.8132 13.3334 14.0065 13.3334H16.6667V8.01292C16.6667 7.86335 16.6187 7.74049 16.5226 7.64434C16.4264 7.54818 16.3035 7.50011 16.154 7.50011H8.01291C7.86334 7.50011 7.74048 7.54818 7.64433 7.64434C7.54818 7.74049 7.5001 7.86335 7.5001 8.01292Z" fill="white" />
                </g>
            </svg>
        </SvgIcon>
    );

    const FavoritedLineupDisplay = () => {
        return (
            <Stack direction='row' alignItems='center' gap='6px' sx={{ fontWeight: 700, fontSize: '18px' }}>
                {exportableLineups?.length > 0 ? <FavoriteIcon sx={{ color: '#FF0000' }} /> : <FavoriteBorderOutlinedIcon />} <span>{exportableLineups?.length} Favorite Lineups</span>
            </Stack>
        )
    };

    React.useEffect(() => {
        if (isAddLineupsSuccess || isRemoveLineupsSuccess) {
            refetchCSV();
            refetchContestSummary();
        }
    }, [isAddLineupsSuccess, isRemoveLineupsSuccess]);

    React.useEffect(() => {
        setLocalSavedLineups(prev => ({ ...prev, saved: savedLineups }))
    }, [savedLineups])

    React.useEffect(() => {
        sessionStorage.setItem('entryManagerEnableDuplicateLineups', JSON.stringify(enableDuplicateLineups));
    }, [enableDuplicateLineups]);
    return (
        <React.Fragment>
            <Button
                onClick={handleClickOpen}
                sx={{
                    backgroundColor: props.bgColor || '#273241',
                    borderRadius: '6px',
                    color: '#fff',
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap',
                    '&.MuiButton-outlined': {
                        textTransform: 'uppercase',
                        backgroundColor: 'unset'
                    }
                }}
                size='small'
                variant={buttonVariant}
                startIcon={props.showIcon && <NoteStackedIconOutline />}
            >
                {buttonLabel} {(showLineupCount && (lineupBuilderLineups.length > 0 || savedLineups.length > 0)) && `(${lineupBuilderLineups.length || savedLineups.length})`}
            </Button>
            <Dialog
                PaperProps={{
                    style: {
                        backgroundColor: '#0A0A0A',
                        maxWidth: '1000px',
                        maxHeight: '80vh',
                        overflow: 'scroll',
                        width: '100%',
                    }
                }}
                sx={{
                    color: '#fff'
                }}
                open={open}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleClose}
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogHeader>
                    <Typography color='#fff' fontFamily='Inter' variant='subtitle1' component='div' fontWeight={500} fontSize='18px' lineHeight='normal'>Entry Manager</Typography>
                    <IconButton color='inherit' onClick={handleClose} aria-label='close drawer' sx={{ p: 0 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogHeader>
                <Stack sx={{ backgroundColor: '#101010', p: '10px 20px', gap: '5px' }}>
                    {
                        contestsData?.length ? (
                            <>
                                <EntryContestRow
                                    contestName={<FavoritedLineupDisplay />}
                                    onSuccess={isFileUploadSuccess}
                                    secondaryText={exportableLineups.length ? 'Select the contest(s) you want to apply these favorited lineups to.' : isFileUploadSuccess ? 'File Successfully Uploaded' : ''}
                                />
                                {isFileUploadError && <Alert variant='outlined' severity='error'>{fileError?.message}</Alert>}
                            </>
                        ) : (
                            <>
                                <Stack
                                    direction='row'
                                    justifyContent='space-between'
                                    alignItems='center'
                                >
                                    <Stack
                                        direction='row'
                                        alignItems='center'
                                        gap='15px'
                                        sx={{
                                            borderRadius: '6px',
                                            border: '1px solid rgba(251, 255, 77, 0.30)',
                                            background: 'rgba(251, 255, 77, 0.04)',
                                            padding: '8px 15px'
                                        }}
                                    >
                                        <Stack gap='4px' width='100%'>
                                            <Typography fontWeight={700} fontSize='18px' color='#fff'>Upload CSV</Typography>
                                            <Typography variant='caption' color='#fff'>You need to upload your {sportsbook?.siteName} CSV entry file to add lineups to the Entry Manager.</Typography>
                                        </Stack>
                                        <WarningAmberOutlinedIcon sx={{ height: '30px', width: '30px', color: 'rgba(251, 255, 77, 0.7)' }} />
                                    </Stack>
                                    <ContestUpload
                                        onUpload={handleFileUpload}
                                        slateId={selectedSlate?.slateId}
                                        isUploading={isUploading}
                                    />
                                </Stack>
                                <Link href={sportsbook?.siteCSVUrl} rel="noopener" target="_blank">
                                    <Typography variant='caption'>Don’t have the CSV? Click here to download it.</Typography>
                                </Link>
                                {isFileUploadError && <Alert variant='outlined' severity='error'>{fileError?.message}</Alert>}
                            </>
                        )
                    }
                </Stack>
                <DialogContent sx={{ p: '15px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Stack
                        direction='row'
                        justifyContent='space-between'
                    >
                        <Stack
                            direction='row'
                            alignItems='center'
                            gap='15px'
                            color='#FFF'
                        >
                            <Typography fontFamily='Inter' fontWeight={700} whiteSpace='nowrap'>My Contests</Typography>
                            <Typography fontFamily='Inter' fontSize='14px'>
                                League: {sport} - Site: {site} - Slate: {selectedSlate.name} ({new Date(selectedSlate.startTime).toLocaleDateString([], { month: 'short', day: '2-digit', timeStyle: undefined })} - {new Date(selectedSlate.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} EST)
                            </Typography>
                            {
                                contestsData?.length > 0 &&
                                <ContestUpload
                                    edit
                                    onUpload={handleFileUpload}
                                    slateId={selectedSlate?.slateId}
                                    isUploading={isUploading}
                                />
                            }
                        </Stack>
                        <ConfirmModal handleRemoveAllLineups={handleRemoveAllLineups} />
                    </Stack>
                    <Stack direction='row'>
                        <FormControl fullWidth sx={{ flexDirection: 'row', alignItems: 'center', color: '#fff' }}>
                            <FormLabel sx={{ color: '#fff', mr: '10px', '&.MuiFormLabel-root.Mui-focused': { color: '#fff' } }}>Sort:</FormLabel>
                            <RadioGroup
                                value={sortBy}
                                onChange={handleSortChange}
                                row
                                aria-label="sortBy"
                            >
                                <FormControlLabel value="contestFee" control={<Radio sx={{ color: '#fff', fontSize: '14px', '&.Mui-checked': { color: '#42a5f5' } }} />} label="Entry Fee" />
                                <FormControlLabel value="entries" control={<Radio sx={{ color: '#fff', fontSize: '14px', '&.Mui-checked': { color: '#42a5f5' } }} />} label="Entries" />
                            </RadioGroup>
                        </FormControl>
                    </Stack>
                    <Stack direction='column' gap='2px'>
                        {
                            hasContests ? contestsData?.map(contest => {
                                const savedEntries = contest?.savedEntries;
                                const totalEntries = contest?.totalEntries;
                                const secondaryText = `${savedEntries}/${totalEntries} Lineups`;
                                const maxExportableLineups = exportableLineups?.slice(0, totalEntries);
                                const hasSavedEntries = savedEntries > 0;
                                const disabled = !maxExportableLineups.length && !savedEntries;
                                const endIcon = (hasSavedEntries && !maxExportableLineups.length) ||
                                    (savedEntries === totalEntries) ? (
                                    isLoadingRemove ? (
                                        <CircularProgress size={18} sx={{ color: '#B0B0B0' }} />
                                    ) : (
                                        <DeleteOutlinedIcon />
                                    )
                                ) : (
                                    isLoadingAdd ? (
                                        <CircularProgress size={18} sx={{ color: '#B0B0B0' }} />
                                    ) : (
                                        <AddIcon />
                                    )
                                );
                                const buttonSx = (hasSavedEntries && !maxExportableLineups.length) || (savedEntries === totalEntries) ? {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                } : {
                                    backgroundColor: '#005CCE',
                                    '&.MuiIconButton-root:hover': {
                                        backgroundColor: '#005CCE'
                                    }
                                }
                                const contestLineups = {
                                    slateId,
                                    contestId: contest?.contestId,
                                    lineups: maxExportableLineups.length > (totalEntries - savedEntries) ? maxExportableLineups?.slice(0, totalEntries - savedEntries) : maxExportableLineups
                                };
                                const params = {
                                    token: userToken,
                                    sport,
                                    contests: [contestLineups]
                                };

                                return (
                                    <EntryContestRow
                                        {...contest}
                                        contestFee={site !== 'FD' ? contest?.contestFee : null}
                                        buttonSx={buttonSx}
                                        disabled={disabled}
                                        endIcon={endIcon}
                                        key={contest?.contestId}
                                        secondaryText={secondaryText}
                                        onClick={(hasSavedEntries && !maxExportableLineups.length) || (savedEntries === totalEntries) ? () => handleRemoveLineupsFromContest(contest?.contestId) : () => handleAddLineupsToContest(params)}
                                        onSuccess={hasSavedEntries}
                                    />
                                )
                            }) : <EntryContestRow contestName='No Contests' secondaryText='0/0 Lineups' />
                        }
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #1C1C1C' }}>
                    <Stack direction='row' gap='15px'>
                        <Typography color='#fff' whiteSpace='nowrap'>{contestsData?.length} Contests</Typography>
                        <Typography color='#fff' whiteSpace='nowrap'>{allContests?.savedEntries}/{allContests?.totalEntries} Lineups</Typography>
                    </Stack>
                    <Stack direction='row' gap='15px'>
                        <ToggleSwitch
                            checked={enableDuplicateLineups}
                            label='Enable Duplicate Lineups'
                            onChange={handleToggleSwitch}
                            tooltipTitle='When enabled, the top ROI lineups will be assigned to fill each contest’s max entry limit and can be used in multiple entries. When disabled, favorited lineups are removed once assigned to a contest, preventing lineups from being used in multiple entries.'
                        />
                        <Button
                            variant='contained'
                            onClick={() => downloadCSV()}
                            endIcon={<DownloadDoneOutlined />}
                            disabled={allContests?.savedEntries === 0}
                            sx={{
                                borderRadius: '6px',
                                backgroundColor: allContests?.savedEntries === allContests?.totalEntries ? '#005CCE' : '#252525',
                                '&.Mui-disabled': {
                                    background: '#252525 !important',
                                    color: '#FFF'
                                },
                                opacity: allContests?.savedEntries === 0 ? '.3' : '1'
                            }}
                        >
                            Download Entry File
                        </Button>
                        <Button
                            variant='contained'
                            onClick={handleClose}
                            sx={{ backgroundColor: allContests?.savedEntries === allContests?.totalEntries ? '#252525' : '#005CCE' }}
                        >
                            Save and Continue
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};